import { whereEq } from 'ramda';
import { getSafeNetworksStore } from '../storage/stores';
import { ActiveSafeNetwork } from '../types/store/safeNetworks';
import { getChromeAlarms } from '../chrome/wrappers';
import { JSONRecord } from '../types/json';
import { createAlarmWithDelay } from '../alarms';
import { updateSafeNetworkStateIfEnabled } from './index';
import { getIdentificationHeader } from '../clientIdentification';

// Remember to clear the alarm if it is ever removed or renamed!
export const CURRENT_SAFE_NETWORK_UPDATE_ALARM_NAME =
  'CURRENT_SAFE_NETWORK_UPDATE_ALARM';

/**
 * Detects and updates the active Safe Network state.
 *
 * - Device is in a SN: Active Safe Network is persisted (and clears retries)
 * - Device is NOT in a SN: Active Safe Network is set as undefined (and clears
 * retries)
 * - Unknown: Retries to update the active Safe Network exponentially
 */
export const updateActiveSafeNetwork = async () => {
  const probeState = await checkProbes();
  const probeQueryFailed = probeState.state === 'unknown';

  if (probeQueryFailed) {
    // assume the current SN didn't change and enqueue retry
    enqueueRetry();
  } else {
    // Clear any enqueued retry and persist current SN state
    await clearProbeRetries();
    if (probeState.state === 'inASafeNetwork') {
      getSafeNetworksStore().setActiveSafeNetwork(probeState.activeSafeNetwork);
    } else {
      getSafeNetworksStore().setActiveSafeNetwork();
    }
  }
  return probeState;
};

/**
 * Queries valid probes and returns the Safe Network state the device is in, and the active Safe Network if any.
 */
const checkProbes = async (): Promise<ProbeState> => {
  let probeQueryFailed = false;
  for (const safeNetwork of getSafeNetworksStore().getValidSafeNetworks()) {
    const probeState = await queryProbe(
      safeNetwork.url,
      safeNetwork.expectedStatusCode,
      safeNetwork.expectedContentMatch
    );
    if (probeState.state === 'inASafeNetwork') {
      return probeState;
    } else if (probeState.state === 'unknown') {
      probeQueryFailed = true;
    }
  }
  if (probeQueryFailed) {
    // If a probe query failed, assume the safe network state didn't change
    console.log("Probe query failed: Safe Network status didn't change.");
    return { state: 'unknown' };
  }
  return { state: 'notInASafeNetwork' };
};

/**
 * Queries a probe to determine if the device is in a Safe Network or not.
 */
const queryProbe = async (
  url: string,
  expectedStatus: number,
  expectedContent: JSONRecord[]
): Promise<ProbeState> => {
  let response: Response;
  try {
    const headers = new Headers({
      ...getIdentificationHeader(),
    });
    response = await fetch(url, {
      cache: 'no-store',
      headers: headers,
    });
  } catch (error) {
    // Network error, we can't know the SN status.
    console.warn(error);
    return { state: 'unknown' };
  }
  if (response.status === expectedStatus) {
    // The status code matches, check for matching content.
    return checkProbeContent(url, response, expectedContent);
  } else if (response.status >= 500 && response.status <= 599) {
    // Server error (and the status code was not expected), we can't know the SN status.
    return { state: 'unknown' };
  }
  // Another unmaching status code, the device is not in a SN.
  return { state: 'notInASafeNetwork' };
};

/**
 * Checks the probe response content to determine if the device is in a Safe Network or not.
 */
const checkProbeContent = async (
  url: string,
  response: Response,
  expectedContent: JSONRecord[]
): Promise<ProbeState> => {
  let content: JSONRecord;
  try {
    content = await response.json();
  } catch (error) {
    // Parse error, we can't know the SN status.
    console.warn(error);
    return { state: 'unknown' };
  }
  for (const expected of expectedContent) {
    if (whereEq(expected, content)) {
      // If content matches, the device is in a SN.
      return {
        state: 'inASafeNetwork',
        activeSafeNetwork: {
          url,
          response: content,
        },
      };
    }
  }
  // If content doesn't match, the device is not in a SN.
  return { state: 'notInASafeNetwork' };
};

export const onUpdateActiveSafeNetworkRetry = async () => {
  await updateSafeNetworkStateIfEnabled();
  incrementRetries();
};

const incrementRetries = () => {
  const currentRetries = getSafeNetworksStore().getProbeRetries();
  getSafeNetworksStore().setProbeRetries(currentRetries + 1);
};

/**
 * Enqueues a Safe Networks update retry.
 *
 * The delay will increase exponentially: 1min, 2min, 4min, 8min...
 */
const enqueueRetry = () => {
  const currentRetries = getSafeNetworksStore().getProbeRetries();
  createAlarmWithDelay(
    CURRENT_SAFE_NETWORK_UPDATE_ALARM_NAME,
    2 ** currentRetries
  );
};

export const clearProbeRetries = () => {
  getSafeNetworksStore().setProbeRetries(0);
  return getChromeAlarms().clear(CURRENT_SAFE_NETWORK_UPDATE_ALARM_NAME);
};

export type ProbeState = InASafeNetwork | NotInASafeNetwork | Unknown;
interface InASafeNetwork {
  state: 'inASafeNetwork';
  activeSafeNetwork: ActiveSafeNetwork;
}
interface NotInASafeNetwork {
  state: 'notInASafeNetwork';
}
interface Unknown {
  state: 'unknown';
}
